import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  getCardinality,
  postCardinalitySave,
  type CardinalityResponse,
  type PostCardinalitySaveData,
} from 'api/generated'
import { toast } from 'sonner'

interface CardinalityState {
  loading: boolean
  error: string | null
  year: number | null
  cardinality: number | null
}

const initialState: CardinalityState = {
  loading: false,
  error: null,
  year: null,
  cardinality: null,
}

export const fetchNewCardinality = createAsyncThunk<
  CardinalityResponse,
  void,
  { rejectValue: string }
>('cardinality/fetchNewCardinality', async (_, { rejectWithValue }) => {
  try {
    return await unwrap(getCardinality())
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to fetch cardinality.'))
  }
})

export const saveCardinality = createAsyncThunk<
  void,
  PostCardinalitySaveData['query'],
  { rejectValue: string }
>('cardinality/save', async (query, { rejectWithValue }) => {
  try {
    await unwrap(postCardinalitySave({ query }))
    toast.success('Operazione eseguita con successo.')
  } catch (error) {
    toast.error("Impossibile eseguire l'operazione.")
    return rejectWithValue(errorMessage(error, 'Failed to save cardinality.'))
  }
})

const cardinalitySlice = createSlice({
  name: 'cardinality',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewCardinality.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNewCardinality.fulfilled, (state, action) => {
        state.loading = false
        state.year = action.payload.year
        state.cardinality = action.payload.cardinality
      })
      .addCase(fetchNewCardinality.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to fetch cardinality.'
      })
      .addCase(saveCardinality.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveCardinality.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(saveCardinality.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Failed to save cardinality.'
      })
  },
})

export const cardinalityReducer = cardinalitySlice.reducer
